import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, ArrowLeft, CheckCircle2, AlertCircle, Loader2, Check } from "lucide-react";
import { SEO } from "@/components/SEO";
import { HERO_IMAGES } from "@/data/products";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";
import { trackEvent, trackMetaEvent, getSessionId } from "@/components/Analytics";
import { goToEnquiryAs } from "@/components/EnquiryForm";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = "/products/bushcabin/bushcabin-tile-main-closed-beach.webp";
const SIDES = ["Left", "Right"];
const WALL_KIT_TRIGGER_SLUG = "quick-pitch-weathershade-20sec";
const WALL_KIT_SLUG = "wall-kit-quick-pitch";
const AWNING_BRACKET_SLUG = "awning-bracket-quick-pitch";
const AWNING_SLUGS = [WALL_KIT_TRIGGER_SLUG, "quick-pitch-weathershade-maxx-2m"];
// The Hilux Double Cab's base-camper image in the builder should match the
// Bush Cabin page's own hero shot, rather than its catalogue thumbnail.
const BASE_CABIN_IMAGE_OVERRIDES = { "hilux-double-cab": HERO_IMAGES.bushcabin };
// Remembers an in-progress build for this tab so a customer who clicks
// through to a model's detail page (to read more) and comes back isn't
// dropped back to a blank build.
const BUILD_STATE_KEY = "xz-build-your-bushcabin-v1";
const fmt = (n) => Math.round(n || 0).toLocaleString("en-NZ");

// Accessories with a "side" option group (Rear MOLLE Panel, Side Door MOLLE
// Panel) can be bought for one side, both sides, or neither — and each side
// can independently have its own other options (e.g. Standard Fixed on the
// left, Drop Down on the right). Everything else is a single toggle.
const isSideAware = (accessory) => (accessory.optionGroups || []).some((g) => g.key === "side");
const otherGroups = (accessory) => (accessory.optionGroups || []).filter((g) => g.key !== "side");

// Every option group defaults to whichever choice adds the least cost
// (normally the $0 / included choice).
const defaultOptionsFor = (groups) => {
  const out = {};
  (groups || []).forEach((g) => {
    const cheapest = [...g.choices].sort((a, b) => a.priceDelta - b.priceDelta)[0];
    if (cheapest) out[g.key] = cheapest.label;
  });
  return out;
};

const initialSelection = (accessory) => {
  if (isSideAware(accessory)) {
    const groups = otherGroups(accessory);
    return { sides: { Left: { enabled: false, options: defaultOptionsFor(groups) }, Right: { enabled: false, options: defaultOptionsFor(groups) } } };
  }
  return { enabled: false, options: defaultOptionsFor(accessory.optionGroups) };
};

const isChosen = (accessory, sel) =>
  isSideAware(accessory) ? SIDES.some((side) => sel?.sides?.[side]?.enabled) : !!sel?.enabled;

// Flattens one accessory's selection into 0, 1 or 2 priced line items —
// a side-aware accessory can produce a separate line per side, each with
// its own options and price.
const lineItems = (accessory, sel) => {
  if (isSideAware(accessory)) {
    const groups = otherGroups(accessory);
    return SIDES.filter((side) => sel?.sides?.[side]?.enabled).map((side) => {
      const s = sel.sides[side];
      let price = accessory.price || 0;
      const parts = [side];
      groups.forEach((g) => {
        const label = s.options?.[g.key];
        const choice = g.choices.find((c) => c.label === label);
        if (choice) price += choice.priceDelta || 0;
        if (label) parts.push(label);
      });
      return { key: `${accessory.slug}-${side}`, label: `${accessory.name} (${parts.join(", ")})`, price };
    });
  }
  if (!sel?.enabled) return [];
  let price = accessory.price || 0;
  const parts = [];
  (accessory.optionGroups || []).forEach((g) => {
    const label = sel.options?.[g.key];
    const choice = g.choices.find((c) => c.label === label);
    if (choice) price += choice.priceDelta || 0;
    if (label) parts.push(label);
  });
  return [{ key: accessory.slug, label: parts.length ? `${accessory.name} (${parts.join(", ")})` : accessory.name, price }];
};

const PillGroup = ({ choices, activeLabel, onSelect, testidPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {choices.map((c) => {
      const active = activeLabel === c.label;
      return (
        <button
          key={c.label}
          type="button"
          data-testid={`${testidPrefix}-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onSelect(c.label)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-wider border transition-colors ${active ? "border-[var(--brand-primary)] text-[var(--brand-primary)] bg-[var(--brand-primary)]/10" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-white/40"}`}
        >
          {active && <Check size={12} />}
          {c.label}{c.priceDelta ? ` +$${fmt(c.priceDelta)}` : ""}
        </button>
      );
    })}
  </div>
);

// One accessory that has independent left/right selection.
const SideAwareAccessory = ({ accessory, sel, onToggleSide, onSetSideOption }) => {
  const groups = otherGroups(accessory);
  return (
    <div className="mt-3 space-y-4">
      {SIDES.map((side) => {
        const s = sel.sides[side];
        return (
          <div key={side}>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-2">{side}</p>
            {groups.length === 0 ? (
              <div className="flex items-center gap-3">
                <Switch
                  checked={s.enabled}
                  onCheckedChange={() => onToggleSide(side)}
                  data-testid={`build-accessory-side-toggle-${accessory.slug}-${side.toLowerCase()}`}
                />
                <span className="text-sm text-[var(--text-secondary)]">{s.enabled ? `$${fmt(accessory.price)}` : "Not included"}</span>
              </div>
            ) : (
              <PillGroup
                choices={[{ label: "None", priceDelta: 0 }, ...groups[0].choices]}
                activeLabel={s.enabled ? s.options?.[groups[0].key] : "None"}
                testidPrefix={`build-accessory-side-option-${accessory.slug}-${side.toLowerCase()}`}
                onSelect={(label) => {
                  if (label === "None") { onToggleSide(side, false); return; }
                  onSetSideOption(side, groups[0].key, label);
                  onToggleSide(side, true);
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

// One product card: single toggle + option-group pills, OR (for MOLLE
// panels) independent left/right selection via SideAwareAccessory. `extra`
// renders any additional nested content once the item is enabled (used for
// the conditional wall kit under the 20 Sec awning). `onShowInfo` opens a
// modal with the full product info — instead of navigating to the real
// product page, which would risk the customer being diverted into an
// Add to Cart flow and losing their place in the configurator.
const AccessoryCard = ({ accessory: a, sel, onToggle, onSetOption, onToggleSide, onSetSideOption, onShowInfo, extra }) => {
  if (!sel) return null;
  const sideAware = isSideAware(a);
  const active = isChosen(a, sel);
  return (
    <div
      data-testid={`build-accessory-card-${a.slug}`}
      className={`border flex flex-col sm:flex-row transition-colors overflow-hidden ${active ? "border-[var(--brand-primary)] bg-[var(--bg-secondary)]" : "border-[var(--border-default)] bg-[var(--bg-secondary)]/50"}`}
    >
      <button type="button" onClick={() => onShowInfo(a)} data-testid={`build-accessory-detail-link-${a.slug}`} className="flex-shrink-0 w-full sm:w-56 md:w-72 sm:self-stretch overflow-hidden group block">
        <img src={a.thumb} alt={a.name} className="w-full h-full aspect-[4/3] sm:aspect-auto object-cover group-hover:opacity-80 transition-opacity" />
      </button>
      <div className="flex-1 min-w-0 p-4 md:p-5">
        <div className="min-w-0">
          <button type="button" onClick={() => onShowInfo(a)} className="text-left hover:text-[var(--brand-primary)] transition-colors">
            <h3 className="font-display text-base md:text-lg text-white leading-tight">{a.name}</h3>
          </button>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {a.inStock === false || a.priceOnRequest || a.price == null
              ? (a.badge || "Coming Soon")
              : `${a.priceFrom ? "FROM " : ""}$${fmt(a.price)}${sideAware ? " per side" : ""}`}
          </p>
          <button
            type="button"
            onClick={() => onShowInfo(a)}
            data-testid={`build-accessory-view-details-${a.slug}`}
            className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[var(--brand-primary)] hover:underline mt-2"
          >
            View Details <ArrowUpRight size={12} />
          </button>
        </div>

        {!sideAware && a.inStock === false && (
          <p className="mt-3 text-sm font-mono uppercase tracking-wider text-[var(--brand-primary)]" data-testid={`build-accessory-not-available-${a.slug}`}>
            {a.availability || "Coming Soon — Not Yet Available To Add"}
          </p>
        )}

        {!sideAware && a.inStock !== false && (
          <div className="mt-3 flex items-center gap-3">
            <Switch
              checked={sel.enabled}
              onCheckedChange={onToggle}
              data-testid={`build-accessory-toggle-${a.slug}`}
            />
            <span className="text-sm text-[var(--text-secondary)]">{sel.enabled ? `$${fmt(a.price)}` : "Not included"}</span>
          </div>
        )}

        {sideAware && (
          <SideAwareAccessory accessory={a} sel={sel} onToggleSide={onToggleSide} onSetSideOption={onSetSideOption} />
        )}

        {!sideAware && sel.enabled && (a.optionGroups || []).length > 0 && (
          <div className="mt-3 space-y-3">
            {a.optionGroups.map((g) => (
              <div key={g.key}>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-2">{g.label}</p>
                <PillGroup
                  choices={g.choices}
                  activeLabel={sel.options?.[g.key]}
                  testidPrefix={`build-accessory-option-${a.slug}-${g.key}`}
                  onSelect={(label) => onSetOption(g.key, label)}
                />
              </div>
            ))}
          </div>
        )}

        {!sideAware && sel.enabled && extra}
      </div>
    </div>
  );
};

// Full product info shown in-place (gallery, description, features) so the
// customer can read up on an accessory without leaving the configurator —
// closing it returns exactly to where they were, selections and scroll
// position untouched. Uses the same ProductGallery (single large image +
// left/right arrows) as every real product detail page, rather than
// dumping every photo into a grid.
const AccessoryInfoModal = ({ accessory, onClose }) => {
  const gallery = useMemo(() => {
    if (!accessory) return [];
    return (accessory.gallery || []).length > 0
      ? accessory.gallery.map(normalizeGalleryImage)
      : [{ src: accessory.thumb, alt: null, type: "image", pos: "center" }];
  }, [accessory]);

  // Option groups that actually change the price (e.g. "Drop Down +$200")
  // — shown under the base price so it's clear the price varies by choice.
  // The "side" group (Left/Right) never carries a price delta, so it's excluded.
  const pricedGroups = useMemo(
    () => (accessory?.optionGroups || []).filter((g) => g.key !== "side" && g.choices.some((c) => c.priceDelta)),
    [accessory]
  );

  return (
    <Dialog open={!!accessory} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="accessory-info-modal" className="max-w-2xl bg-[var(--bg-secondary)] border-[var(--border-default)] text-white max-h-[85vh] overflow-y-auto">
        {accessory && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-white pr-6">{accessory.name}</DialogTitle>
              {accessory.tagline && <DialogDescription className="text-base text-[var(--text-secondary)]">{accessory.tagline}</DialogDescription>}
            </DialogHeader>
            <ProductGallery gallery={gallery} productName={accessory.name} thumbGridClassName="grid-cols-5" />
            {accessory.intro && <p className="text-base text-[var(--text-secondary)] leading-relaxed">{accessory.intro}</p>}
            {(accessory.features || []).length > 0 && (
              <ul className="space-y-2 text-base text-[var(--text-secondary)]">
                {accessory.features.map((f, i) => (
                  <li key={i} className="flex gap-2"><Check size={16} className="text-[var(--brand-primary)] flex-shrink-0 mt-1" />{f}</li>
                ))}
              </ul>
            )}
            <div>
            {accessory.inStock === false || accessory.priceOnRequest || accessory.price == null ? (
                <p data-testid="accessory-info-price" className="font-display text-xl text-[var(--brand-primary)]">
                  {accessory.badge || "Coming Soon"}
                </p>
              ) : (
                <p data-testid="accessory-info-price" className="font-display text-xl text-[var(--brand-primary)]">
                  {accessory.priceFrom ? "FROM " : ""}${fmt(accessory.price)}{isSideAware(accessory) ? " per side" : ""}
                </p>
              )}
              {pricedGroups.map((g) => (
                <p key={g.key} data-testid={`accessory-info-price-note-${g.key}`} className="text-sm text-[var(--text-secondary)] mt-1">
                  {g.choices.map((c) => `${c.label} ${c.priceDelta ? `+$${fmt(c.priceDelta)}` : "included"}`).join("  ·  ")}
                </p>
              ))}
            </div>
            {/* This popup never navigates away — closing it (this button, X,
                Escape, or clicking outside) just returns to the exact same
                spot in the build. Worded the same as the cabin flow's real
                "Back To Builder" page-navigation button, for consistency,
                even though nothing actually navigates here. */}
            <button
              type="button"
              onClick={onClose}
              data-testid="accessory-info-back-to-builder"
              className="btn-primary justify-self-start"
            >
              <ArrowLeft size={14} /> Back To Builder
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function BuildYourBushCabinPage() {
  const [cabins, setCabins] = useState([]); // selectable base Bush Cabins (by vehicle)
  const [cabinSlug, setCabinSlug] = useState(null);
  const [accessories, setAccessories] = useState([]);
  const [awnings, setAwnings] = useState([]);
  const [wallKitProduct, setWallKitProduct] = useState(null);
  const [wallKitChoice, setWallKitChoice] = useState("None");
  const [loadFailed, setLoadFailed] = useState(false);
  const [selections, setSelections] = useState({});
  const [showContact, setShowContact] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitState, setSubmitState] = useState({ loading: false, ok: false, error: null });
  const [infoAccessory, setInfoAccessory] = useState(null);

  const cabin = cabins.find((c) => c.slug === cabinSlug) || null;
  // Real photos only (gallery may also contain YouTube video URLs mixed in) —
  // used to make the "View Details & Photos" link below show an actual
  // preview thumbnail + accurate count instead of being a plain text link
  // that's easy to miss mid-build.
  const cabinPhotos = (cabin?.gallery || []).filter((g) => {
    const src = typeof g === "string" ? g : g?.src;
    return src && !src.includes("youtube");
  });

  // Fires the same Meta "ViewContent" signal as visiting the cabin's own
  // product page (same content path/id) — so Meta ad campaigns already
  // optimised around Bush Cabin product views keep getting that signal
  // even when a customer configures here instead of clicking through.
  useRecentlyViewed(
    cabin && { path: `/bush-cabin/${cabin.slug}`, name: cabin.name, thumb: cabin.thumb, price: cabin.price, priceFrom: cabin.priceFrom }
  );

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/products/bushcabin`),
      axios.get(`${API}/api/products/bushcabinaccessory`),
      axios.get(`${API}/api/products/awning`),
    ]).then(([cabinRes, accRes, awningRes]) => {
      // Only vehicle-based cabins with a fixed price can anchor a running
      // total — the "Turn-Key Package" (vehicle + cabin bundle) is priced
      // on request and is a different sales motion, so it's excluded here.
      const priced = (cabinRes.data || []).filter((c) => !c.priceOnRequest);
      setCabins(priced);
      // Order here always matches the admin's storefront product order —
      // this list is read straight from the API with no client re-sort.
      const accs = accRes.data || [];
      setAccessories(accs);

      const allAwnings = awningRes.data || [];
      // The Bush Cabin only supports left-side awning fitment with no tent
      // access hatch — those choices aren't compatible, so they're hidden
      // here (the underlying products are unchanged for other pages).
      const selectedAwnings = AWNING_SLUGS
        .map((slug) => allAwnings.find((a) => a.slug === slug))
        .filter(Boolean)
        .map((a) => ({
          ...a,
          optionGroups: (a.optionGroups || [])
            .filter((g) => g.key !== "hatch")
            .map((g) => (g.key === "fitment" ? { ...g, choices: g.choices.filter((c) => c.label !== "Right Side") } : g))
            .filter((g) => g.choices.length > 1),
        }));
      setAwnings(selectedAwnings);
      setWallKitProduct(allAwnings.find((a) => a.slug === WALL_KIT_SLUG) || null);

      const initial = {};
      [...accs, ...selectedAwnings].forEach((a) => { initial[a.slug] = initialSelection(a); });

      // Restore a previous in-progress build from this tab's session, if
      // one exists — merged onto fresh defaults so a changed catalogue
      // (new/removed accessory) can't produce a broken shape.
      let restored = null;
      try { restored = JSON.parse(sessionStorage.getItem(BUILD_STATE_KEY) || "null"); } catch { restored = null; }
      if (restored?.selections) {
        Object.keys(initial).forEach((slug) => {
          if (restored.selections[slug]) initial[slug] = restored.selections[slug];
        });
      }
      setSelections(initial);
      setCabinSlug(restored?.cabinSlug && priced.some((c) => c.slug === restored.cabinSlug) ? restored.cabinSlug : priced[0]?.slug || null);
      if (restored?.wallKitChoice) setWallKitChoice(restored.wallKitChoice);
    }).catch((err) => { console.error(err); setLoadFailed(true); });
  }, []);

  // Persists the in-progress build so it survives a trip to a model's
  // detail page and back. Skipped until the catalogue has loaded, so the
  // pre-hydration default state can't stomp on a saved build.
  useEffect(() => {
    if (!cabins.length) return;
    sessionStorage.setItem(BUILD_STATE_KEY, JSON.stringify({ cabinSlug, selections, wallKitChoice }));
  }, [cabins.length, cabinSlug, selections, wallKitChoice]);

  const toggleAccessory = (slug) => {
    setSelections((s) => {
      const nextEnabled = !s[slug]?.enabled;
      if (slug === WALL_KIT_TRIGGER_SLUG && !nextEnabled) setWallKitChoice("None");
      const next = { ...s, [slug]: { ...s[slug], enabled: nextEnabled } };
      // Selecting either awning implies you'll need a mounting bracket —
      // switch it on automatically as a convenience default (still
      // manually removable, e.g. if the customer already owns one).
      if (AWNING_SLUGS.includes(slug) && nextEnabled && next[AWNING_BRACKET_SLUG]) {
        next[AWNING_BRACKET_SLUG] = { ...next[AWNING_BRACKET_SLUG], enabled: true };
      }
      return next;
    });
  };

  const setOption = (slug, groupKey, label) => {
    setSelections((s) => ({ ...s, [slug]: { ...s[slug], options: { ...s[slug]?.options, [groupKey]: label } } }));
  };

  const toggleSide = (slug, side, forceValue) => {
    setSelections((s) => {
      const current = s[slug].sides[side];
      const enabled = forceValue !== undefined ? forceValue : !current.enabled;
      return { ...s, [slug]: { ...s[slug], sides: { ...s[slug].sides, [side]: { ...current, enabled } } } };
    });
  };

  const setSideOption = (slug, side, groupKey, label) => {
    setSelections((s) => {
      const current = s[slug].sides[side];
      return { ...s, [slug]: { ...s[slug], sides: { ...s[slug].sides, [side]: { ...current, options: { ...current.options, [groupKey]: label } } } } };
    });
  };

  const allLineItems = useMemo(() => {
    const items = [...accessories, ...awnings].flatMap((a) => lineItems(a, selections[a.slug]));
    const trigger = selections[WALL_KIT_TRIGGER_SLUG];
    if (trigger?.enabled && wallKitChoice !== "None" && wallKitProduct) {
      const price = wallKitProduct.price + (wallKitChoice === "Full Kit" ? 946 : 0);
      items.push({ key: "wall-kit", label: `${wallKitProduct.name} (270 degree, ${wallKitChoice})`, price });
    }
    return items;
  }, [accessories, awnings, selections, wallKitChoice, wallKitProduct]);

  const total = useMemo(() => {
    const base = cabin?.price || 0;
    return base + allLineItems.reduce((sum, li) => sum + li.price, 0);
  }, [cabin, allLineItems]);

  // "Reached bottom" = the sentinel right after the last accessory/awning
  // option has been visible at least once — i.e. this visitor actually saw
  // every option, not just the first couple before bouncing. Feeds the
  // Admin Panel's builds list, which only shows genuinely-engaged sessions.
  const [reachedBottom, setReachedBottom] = useState(false);
  const bottomSentinelRef = useRef(null);
  useEffect(() => {
    if (reachedBottom) return;
    const el = bottomSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) setReachedBottom(true);
    }, { threshold: 0.01 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [reachedBottom, awnings.length]);

  // Debounced save of the in-progress build, so the Admin Panel can show
  // real builds people put together — whether or not they ever submit the
  // enquiry form below.
  useEffect(() => {
    if (!cabin) return;
    const timer = setTimeout(() => {
      axios.post(`${API}/api/bushcabin-builds`, {
        session_id: getSessionId(),
        cabin_slug: cabin.slug,
        cabin_name: cabin.name,
        total_price: total,
        reached_bottom: reachedBottom,
        summary: allLineItems.map((li) => `${li.label} — $${fmt(li.price)}`),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [cabin, total, reachedBottom, allLineItems]);

  const buildSummaryText = () => {
    const lines = [`Base: ${cabin.name} — $${fmt(cabin.price)}`];
    if (allLineItems.length) {
      lines.push("", "Accessories:");
      allLineItems.forEach((li) => lines.push(`- ${li.label} — $${fmt(li.price)}`));
    }
    lines.push("", `TOTAL: $${fmt(total)}`);
    return lines.join("\n");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitState({ loading: true, ok: false, error: null });
    try {
      const message = `${buildSummaryText()}${contact.notes.trim() ? `\n\nAdditional notes:\n${contact.notes.trim()}` : ""}`;
      const payload = {
        name: contact.name.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim() || null,
        vehicle: cabin.name,
        product: "Build Your Bush Cabin (Configurator)",
        message,
      };
      const { data } = await axios.post(`${API}/api/enquiry`, payload);
      trackEvent("bushcabin_build_enquiry_submit");
      trackMetaEvent("Lead", { content_name: "Build Your Bush Cabin" }, data.meta_event_id);
      setSubmitState({ loading: false, ok: true, error: null });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please email support@xpeditionz.co.nz directly.";
      setSubmitState({ loading: false, ok: false, error: detail });
    }
  };

  if (!cabin) {
    if (loadFailed) {
      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-center px-4" data-testid="build-cabin-load-error">
          <p className="text-white/70 font-mono text-sm">Couldn't load the Bush Cabin builder. Please check your connection and try again.</p>
          <button type="button" onClick={() => window.location.reload()} className="text-[var(--brand-primary)] underline text-sm" data-testid="build-cabin-retry-button">Retry</button>
        </div>
      );
    }
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="build-cabin-page">
      <SEO
        path="/build-your-bush-cabin"
        title="Build Your Bush Cabin · Configure & Price Your Camper"
        description="Pick your Bush Cabin and add the accessories you want — see your price update live, then send us your build for a quote."
        image={HERO_IMG}
      />

      <section className="relative min-h-[70svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Bush Cabin" className="w-full h-full object-cover" style={{ objectPosition: "50% 35%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-14 pt-32 w-full">
          <Link to="/bush-cabin" data-testid="build-cabin-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)] transition-colors mb-6 inline-block">
            ← Bush Cabin Ute Camper
          </Link>
          <p className="overline mb-3">— Configure Your Build</p>
          <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.95] hero-text-shadow">
            BUILD YOUR <span className="text-[var(--brand-primary)]">BUSH CABIN.</span>
          </h1>
        </div>
      </section>

      <div className="container-x py-10 md:py-16 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 lg:gap-14 items-start">
        <div>
          {/* Base cabin */}
          <p className="overline mb-4">— Base Camper</p>
          <div data-testid="build-base-cabin" className="border border-[var(--border-default)] bg-[var(--bg-secondary)] mb-8 flex flex-col sm:flex-row overflow-hidden">
            <img src={BASE_CABIN_IMAGE_OVERRIDES[cabin.slug] || cabin.thumb} alt={cabin.name} className="w-full sm:w-64 md:w-80 aspect-[4/3] sm:aspect-auto sm:self-stretch object-cover flex-shrink-0" />
            <div className="p-5 md:p-6 flex flex-col justify-center">
              <p className="overline mb-2">— Choose Your Bush Cabin</p>
              {cabins.length > 1 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {cabins.map((c) => {
                    const active = c.slug === cabinSlug;
                    return (
                      <button
                        key={c.slug}
                        type="button"
                        data-testid={`build-cabin-option-${c.slug}`}
                        onClick={() => setCabinSlug(c.slug)}
                        className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border transition-colors ${active ? "border-[var(--brand-primary)] text-[var(--brand-primary)] bg-[var(--brand-primary)]/10" : "border-[var(--border-default)] text-[var(--text-secondary)] hover:border-white/40"}`}
                      >
                        {active && <Check size={12} className="inline mr-1.5 -mt-0.5" />}
                        {c.name.replace(/^Bush Cabin\s*—\s*/, "")}
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-xs font-mono text-[var(--text-secondary)] mb-3" data-testid="build-vehicle-not-listed-note">
                Don't have one of these?{" "}
                <button
                  type="button"
                  onClick={() => goToEnquiryAs("Bush Cabin Ute Camper")}
                  data-testid="build-vehicle-not-listed-cta"
                  className="text-[var(--brand-primary)] underline hover:no-underline"
                >
                  Drop us a note
                </button>{" "}
                with what vehicle you have or are looking to purchase.
              </p>
              <h2 className="font-display text-xl md:text-2xl text-white leading-tight">{cabin.name}</h2>
              <p data-testid="build-base-cabin-price" className="font-display text-lg text-[var(--brand-primary)] mt-1">{cabin.priceFrom ? "FROM " : ""}${fmt(cabin.price)}</p>
              <Link
                to={`/bush-cabin/${cabin.slug}?from=build`}
                data-testid="build-base-cabin-view-details"
                className="group inline-flex items-center gap-3 mt-4 self-start border border-[var(--border-default)] hover:border-[var(--brand-primary)] bg-[var(--bg-main)] pl-2 pr-4 py-2 transition-colors"
              >
                {cabinPhotos[1] && (
                  <img
                    src={typeof cabinPhotos[1] === "string" ? cabinPhotos[1] : cabinPhotos[1].src}
                    alt=""
                    className="w-10 h-10 object-cover flex-shrink-0"
                  />
                )}
                <span className="font-mono text-xs uppercase tracking-wider text-white group-hover:text-[var(--brand-primary)] transition-colors">
                  View{cabinPhotos.length ? ` ${cabinPhotos.length}` : ""} Photos & Details
                </span>
                <ArrowUpRight size={14} className="text-[var(--brand-primary)] flex-shrink-0" />
              </Link>
            </div>
          </div>

          {/* Accessories */}
          <p className="overline mb-2">— Add Accessories</p>
          <p className="text-[var(--text-secondary)] text-sm font-mono mb-4 max-w-xl" data-testid="build-accessories-factory-install-note">
            Accessories ordered with a new pre-order build come factory installed at no extra
            charge — this doesn't apply to Bush Cabins already in NZ stock. Not decided yet? Start
            with just the base Bush Cabin; these can be added later as you need them.
          </p>
          <div className="space-y-3">
            {accessories.filter((a) => a.slug !== AWNING_BRACKET_SLUG).map((a) => (
              <AccessoryCard
                key={a.slug}
                accessory={a}
                sel={selections[a.slug]}
                onToggle={() => toggleAccessory(a.slug)}
                onSetOption={(groupKey, label) => setOption(a.slug, groupKey, label)}
                onToggleSide={(side, force) => toggleSide(a.slug, side, force)}
                onSetSideOption={(side, groupKey, label) => setSideOption(a.slug, side, groupKey, label)}
                onShowInfo={setInfoAccessory}
              />
            ))}
          </div>

          {/* Awnings — includes the two Quick Pitch awnings plus the
              matching mounting bracket (a bushcabinaccessory product,
              shown here instead of in the main list above). */}
          {awnings.length > 0 && (
            <>
              <p className="overline mb-4 mt-8">— Awnings</p>
              <div className="space-y-3">
                {[...awnings, accessories.find((a) => a.slug === AWNING_BRACKET_SLUG)].filter(Boolean).map((a) => (
                  <AccessoryCard
                    key={a.slug}
                    accessory={a}
                    sel={selections[a.slug]}
                    onToggle={() => toggleAccessory(a.slug)}
                    onSetOption={(groupKey, label) => setOption(a.slug, groupKey, label)}
                    onToggleSide={(side, force) => toggleSide(a.slug, side, force)}
                    onSetSideOption={(side, groupKey, label) => setSideOption(a.slug, side, groupKey, label)}
                    onShowInfo={setInfoAccessory}
                    extra={a.slug === WALL_KIT_TRIGGER_SLUG && wallKitProduct && (
                      <div className="mt-3">
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-2">Wall Kit (Optional — 270°)</p>
                        <PillGroup
                          choices={[
                            { label: "None", priceDelta: 0 },
                            { label: "2 Panel Kit", priceDelta: wallKitProduct.price },
                            { label: "Full Kit", priceDelta: wallKitProduct.price + 946 },
                          ]}
                          activeLabel={wallKitChoice}
                          testidPrefix="build-wall-kit-option"
                          onSelect={setWallKitChoice}
                        />
                      </div>
                    )}
                  />
                ))}
              </div>
            </>
          )}
          <div ref={bottomSentinelRef} data-testid="build-bottom-sentinel" className="h-px" />
        </div>

        {/* Sticky summary */}
        <div className="lg:sticky lg:top-24 border border-[var(--border-default)] bg-[var(--bg-secondary)] p-5 md:p-6">
          <p className="overline mb-4">— Your Build</p>
          <div data-testid="build-summary-items" className="space-y-2 text-sm mb-4 pb-4 border-b border-[var(--border-default)]">
            <div className="flex justify-between text-[var(--text-secondary)]">
              <span className="truncate pr-2">{cabin.name}</span>
              <span className="flex-shrink-0">${fmt(cabin.price)}</span>
            </div>
            {allLineItems.map((li) => (
              <div key={li.key} className="flex justify-between text-[var(--text-secondary)]">
                <span className="truncate pr-2">{li.label}</span>
                <span className="flex-shrink-0">${fmt(li.price)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-baseline mb-6">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Total</span>
            <span data-testid="build-total-price" className="font-display text-2xl md:text-3xl text-[var(--brand-primary)]">${fmt(total)}</span>
          </div>

          {!showContact ? (
            <button
              type="button"
              data-testid="build-enquire-button"
              onClick={() => setShowContact(true)}
              className="btn-primary w-full justify-center"
            >
              Enquire About This Build <ArrowUpRight size={14} />
            </button>
          ) : submitState.ok ? (
            <p data-testid="build-enquiry-success" className="flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 size={16} /> Thanks — we'll be in touch with a quote shortly.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <input
                type="text"
                required
                placeholder="Your full name"
                data-testid="build-enquiry-name"
                value={contact.name}
                onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                className="form-input"
              />
              <input
                type="email"
                required
                placeholder="you@example.com"
                data-testid="build-enquiry-email"
                value={contact.email}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                className="form-input"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                data-testid="build-enquiry-phone"
                value={contact.phone}
                onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                className="form-input"
              />
              <textarea
                rows={3}
                placeholder="Anything else to add? (optional)"
                data-testid="build-enquiry-message"
                value={contact.notes}
                onChange={(e) => setContact((c) => ({ ...c, notes: e.target.value }))}
                className="form-input resize-y"
              />
              <button
                type="submit"
                disabled={submitState.loading}
                data-testid="build-enquiry-submit"
                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitState.loading ? (<><Loader2 size={14} className="animate-spin" /> Sending…</>) : (<>Send My Build <ArrowUpRight size={14} /></>)}
              </button>
              {submitState.error && (
                <p data-testid="build-enquiry-error" className="flex items-center gap-2 text-sm text-red-400">
                  <AlertCircle size={16} /> {submitState.error}
                </p>
              )}
            </form>
          )}
        </div>
      </div>

      <AccessoryInfoModal accessory={infoAccessory} onClose={() => setInfoAccessory(null)} />
    </div>
  );
}
